import * as React from 'react';

import {CTABannerWithButtons} from 'shells/cta-banner/cta-banner-with-buttons';
import type {CTA} from 'shells/cta-banner/cta-banner-with-buttons';
import {Popover} from 'shells/popover/popover';
import {Body} from 'shells/typography/body';

import type {
    CTABanner_demioEvent as DemioEvent,
    CTABanner_upcomingEvents as UpcomingEvent,
} from 'nutshell-graphql-types';

import {getSubtitle, getTitle} from './helpers';
import {UpcomingWebcasts} from './upcoming-webcasts';
import {useDemioRegister} from './use-demio-register';

type PopoverType = 'changing-reservation' | 'other-times';

type Props = {
    title: string;
    subtitle: string;
    image: {
        src: string;
        width: number;
    };
    demioEvent: DemioEvent;
    upcomingEvents: UpcomingEvent[];
    onDismiss?: () => any;
    centerBannerContent?: boolean;
    includeImage: boolean;
    includeBody: boolean;
    includeRemoveButton: boolean;
    fullSizeImage?: boolean;
    useDoyleTitle?: boolean;
};

export function CTABannerDemio(props: Props) {
    const {
        title,
        subtitle = 'See what makes Nutshell sneaky-powerful and get your questions answered live by our growth team.',
        image,
        demioEvent,
        upcomingEvents,
        onDismiss,
        centerBannerContent,
        includeImage,
        includeBody,
        includeRemoveButton,
        fullSizeImage,
        useDoyleTitle,
    } = props;

    const [popoverType, setPopoverType] = React.useState<PopoverType | null | undefined>(null);
    const buttonRef = React.useRef(undefined);
    const register = useDemioRegister();

    const registerForEvent = () => {
        return register({webcast: demioEvent});
    };

    const handleOpenPopover = (type: PopoverType) => {
        setPopoverType(type);
    };

    const handleBlur = () => {
        setPopoverType(null);
    };

    const getPrimaryCTA = (): CTA => {
        return demioEvent.isCurrentUserRegistered
            ? {
                  text: 'Change reservation',
                  buttonVariant: 'text-secondary',
                  onClick: () => handleOpenPopover('changing-reservation'),
                  // @ts-expect-error
                  getButtonRef: (c) => {
                      buttonRef.current = c;
                  },
              }
            : {text: 'Register now', onClick: registerForEvent};
    };

    const titleText = getTitle(title, demioEvent);

    return (
        <>
            <CTABannerWithButtons
                image={!demioEvent.isCurrentUserRegistered ? image : undefined}
                title={
                    useDoyleTitle ? (
                        <Body
                            size='large'
                            useDoyle={true}
                            isBold={true}
                            color='dark'
                            textAlign='left'
                        >
                            {titleText}
                        </Body>
                    ) : (
                        titleText
                    )
                }
                text={getSubtitle(subtitle, demioEvent)}
                primaryCTA={getPrimaryCTA()}
                secondaryCTA={
                    !demioEvent.isCurrentUserRegistered && upcomingEvents
                        ? {
                              text: 'See other times',
                              onClick: () => handleOpenPopover('other-times'),
                              // @ts-expect-error
                              getButtonRef: (c) => {
                                  buttonRef.current = c;
                              },
                          }
                        : undefined
                }
                onDismiss={onDismiss}
                centerBannerContent={centerBannerContent}
                includeImage={includeImage}
                includeBody={includeBody}
                includeRemoveButton={includeRemoveButton}
                fullSizeImage={fullSizeImage}
            />
            {popoverType ? (
                <Popover anchor={buttonRef.current} location='bottom' onBlur={handleBlur}>
                    <div className='pad-16'>
                        <UpcomingWebcasts
                            onClose={handleBlur}
                            upcomingEvents={upcomingEvents}
                            isChangingReservation={popoverType === 'changing-reservation'}
                        />
                    </div>
                </Popover>
            ) : undefined}
        </>
    );
}
